import { useQueryParams } from "@certn/api/src/useQueryParams";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { PaginationContext, usePaginationContext } from "./PaginationContext";
import { ELLIPSIS, usePaginationRange } from "./usePaginationRange";
import { Text } from "../../foundations/typography/Text";
import { Skeleton } from "../../patterns/skeleton/Skeleton";
import { BasicButton } from "../button/BasicButton";
import { GhostButton } from "../button/GhostButton";
import { GhostIconButton } from "../button/GhostIconButton";
import { DropdownMenu } from "../dropdown-menu/DropdownMenu";
import { MenuItem } from "../dropdown-menu/assets/MenuItem";
import { mq, useMediaQuery } from "../utils";
export interface PaginationProps {
  setPage?: (page: number) => void;
  setPageCount?: (page: number) => void;
  page: number;
  pageCount: number;
  total: number;
  testID?: string;
  className?: string;
  isLoading?: boolean;
}

export function Pagination({
  page,
  pageCount,
  total,
  setPage,
  setPageCount,
  testID = "pagination",
  className,
  isLoading,
}: PaginationProps) {
  const pages = Math.ceil(total / pageCount);
  const range = usePaginationRange(pages, pageCount, page);
  const { mediaQuery } = useMediaQuery();

  return (
    <PaginationContext.Provider
      value={{
        range,
        page,
        pageCount,
        total,
        pages,
        setPage,
        setPageCount,
      }}
    >
      <div
        className={css(
          mediaQuery({
            flexWrap: ["wrap", "unset"],
            gap: ["var(--space-6)"],
            justifyContent: ["center", "space-between"],
            flexDirection: [pages === 1 ? "column" : undefined, "row"],
            display: ["flex"],
            alignItems: ["center"],
            paddingTop: "var(--space-3)",
            paddingBottom: "var(--space-3)",
          }),
          className
        )}
        data-testid={testID}
      >
        <>
          {isLoading ? (
            <div className={styles.skeleton}>
              <Skeleton width="100px" />
              <div className={styles.skeletonPages}>
                <Skeleton width="100px" />
                <Skeleton width="100px" />
              </div>
            </div>
          ) : (
            <>
              <PaginationCount />
              <Pages />
              <PageSize />
            </>
          )}
        </>
      </div>
    </PaginationContext.Provider>
  );
}

const Pages = () => {
  const { setParam } = useQueryParams();
  const { page, range, pages, setPage } = usePaginationContext();
  const { mediaQuery } = useMediaQuery();
  const pageLinks =
    range &&
    pages > 1 &&
    range.map((pageNumber, index) => {
      if (pageNumber === ELLIPSIS) {
        return <Text key={pageNumber + index}>...</Text>;
      }
      if (typeof pageNumber === "number") {
        return (
          <GhostButton
            size="small"
            hoverFill={
              pageNumber === page
                ? "background-inverse"
                : "background-ghost-hover"
            }
            onPress={() => {
              setPage?.(pageNumber);
              setParam("page", pageNumber.toString());
            }}
            fill={page === pageNumber ? "background-inverse" : undefined}
            key={pageNumber}
            testID="pagination-page-number-button"
          >
            <Text
              style="global/body-primary"
              color={pageNumber === page ? "text-inverse" : "text-weak"}
            >
              {pageNumber}
            </Text>
          </GhostButton>
        );
      }
    });

  if (pages > 1) {
    return (
      <div
        className={css(
          styles.pages,
          mediaQuery({
            order: [2, "unset"],
          })
        )}
      >
        <div
          className={css(
            mediaQuery({
              display: [
                pages > 1 ? "block" : "none",
                pages > 1 ? "block" : "none",
              ],
            })
          )}
        >
          <GhostIconButton
            size="small"
            color="text-weak"
            isDisabled={page === 1}
            onPress={() => {
              setPage?.(1);
              setParam("page", "1");
            }}
            icon="first_page"
            hoverFill="background-ghost-hover"
            hoverColor="text-base"
            testID="pagination-jump-to-start-button"
          />
        </div>
        <GhostIconButton
          size="small"
          color="text-weak"
          isDisabled={page === 1}
          onPress={() => {
            setPage?.(page - 1);
            setParam("page", String(page - 1));
          }}
          icon="keyboard_arrow_left"
          hoverFill="background-ghost-hover"
          hoverColor="text-base"
          testID="pagination-back-one-page-button"
        />
        {pageLinks}
        <GhostIconButton
          size="small"
          color="text-weak"
          isDisabled={page === pages}
          onPress={() => {
            setPage?.(page + 1);
            setParam("page", String(page + 1));
          }}
          icon="keyboard_arrow_right"
          hoverFill="background-ghost-hover"
          hoverColor="text-base"
          testID="pagination-advance-one-page-button"
        />
        <div
          className={css(
            mediaQuery({
              display: [
                pages > 1 ? "block" : "none",
                pages > 1 ? "block" : "none",
              ],
            })
          )}
        >
          <GhostIconButton
            size="small"
            color="text-weak"
            isDisabled={page === pages}
            onPress={() => {
              setPage?.(pages);
              setParam("page", pages.toString());
            }}
            icon="last_page"
            hoverFill="background-ghost-hover"
            hoverColor="text-base"
            testID="pagination-jump-to-end-button"
          />
        </div>
      </div>
    );
  }
  return null;
};

const PaginationCount = () => {
  const { total, pageCount, page, pages } = usePaginationContext();
  const { t } = useTranslation();
  const itemsDisplayed = `${(page - 1) * pageCount + 1}-${
    page * pageCount <= total ? page * pageCount : total
  }`;

  return (
    <Text
      color="text-weak"
      className={css(
        mq({
          flexGrow: ["unset", undefined, pages > 1 ? 1 : undefined],
          order: [3, "unset"],
        })
      )}
      style="global/body-secondary"
    >
      {t(`{{itemsDisplayed}} of {{totalItems}} items`, {
        itemsDisplayed: total === 0 ? total : itemsDisplayed,
        totalItems: total,
      })}
    </Text>
  );
};

const PageSize = () => {
  const { pageCount, setPageCount } = usePaginationContext();
  const { setParam, params } = useQueryParams();
  const { t } = useTranslation();

  if (!params.page_size && params.page_size !== undefined) {
    setPageCount?.(25);
  }

  return (
    <div className={styles.pageSize}>
      <Text style="global/body-secondary" color="text-weak">
        {t("Show")}
      </Text>
      <DropdownMenu
        onItemAction={(key) => {
          if (key) {
            setPageCount?.(Number(key));
            setParam("page_size", String(key), true);
          }
        }}
        triggerButton={
          <BasicButton
            size="small"
            rightIcon="expand_more"
            testID="pagination-page-display-dropdown"
          >
            <Text>{pageCount.toString()}</Text>
          </BasicButton>
        }
      >
        <MenuItem id="10" testID="pagination-ten-items-page">
          10
        </MenuItem>
        <MenuItem id="25" testID="pagination-twenty-five-items-page">
          25
        </MenuItem>
        <MenuItem id="50" testID="pagination-fifty-items-page">
          50
        </MenuItem>
        <MenuItem id="100" testID="pagination-one-hundred-items-page">
          100
        </MenuItem>
        <MenuItem id="250" testID="pagination-two-fifty-items-page">
          250
        </MenuItem>
      </DropdownMenu>
      <Text style="global/body-secondary" color="text-weak">
        {t("items")}
      </Text>
    </div>
  );
};

const styles = {
  pageSize: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  pages: css({
    display: "flex",
    alignItems: "center",
    gap: ["var(--space-2)"],
    "> button": {
      height: "var(--space-8)",
      minWidth: "var(--space-8)",
    },
  }),
  skeleton: css({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  skeletonPages: css({
    display: "flex",
    gap: ["var(--space-2)"],
  }),
};
